import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import EditOutlined from '@mui/icons-material/EditOutlined';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import TableChartOutlined from '@mui/icons-material/TableChartOutlined';
import api from '../../services/api';
import { AuthContext } from '../../contexts/AuthContext';
import exportToPDF from './exportPDF';
import exportStyledExcel from './exportExcel';
import AppShell from '../../components/layout/AppShell';
import {
  Button,
  PageHeader,
  PageLoader,
  Alert,
  Badge,
  DataTable,
  ConfirmDialog,
} from '../../components/ui';

/**
 * Section headings used to be tinted blue / yellow / green / red / orange /
 * purple with no meaning attached. They're plain headings now — the reader
 * tells sections apart by their names, which is what names are for.
 */
const sectionConfig = [
  {
    key: 'scr',
    title: 'SCR',
    columns: [
      { key: 'time', header: 'Time' },
      { key: 'number', header: 'No', primary: true, className: 'tabular' },
      { key: 'tag', header: 'Tag' },
      { key: 'status', header: 'Status' },
      { key: 'notes', header: 'Notes' },
    ],
  },
  {
    key: 'microphoneStationGroup',
    title: 'Microphone station group',
    columns: [
      { key: 'time', header: 'Time' },
      { key: 'area', header: 'Area', primary: true },
      { key: 'status', header: 'Status' },
      { key: 'notes', header: 'Notes' },
    ],
  },
  {
    key: 'correctiveMaintenance',
    title: 'Corrective maintenance',
    columns: [
      { key: 'time', header: 'Time' },
      { key: 'cmNo', header: 'CM No', primary: true, className: 'tabular' },
      { key: 'status', header: 'Status' },
      { key: 'notes', header: 'Notes' },
    ],
  },
  {
    key: 'systemFaultLog',
    title: 'System log',
    columns: [
      { key: 'time', header: 'Time' },
      { key: 'logType', header: 'Log type', primary: true },
      { key: 'status', header: 'Status' },
      { key: 'notes', header: 'Notes' },
    ],
  },
  {
    key: 'ppm',
    title: 'PPM',
    columns: [
      { key: 'time', header: 'Time' },
      { key: 'ppmNo', header: 'PPM No', primary: true, className: 'tabular' },
      { key: 'status', header: 'Status' },
      { key: 'notes', header: 'Notes' },
    ],
  },
  {
    key: 'others',
    title: 'Others',
    columns: [
      { key: 'time', header: 'Time' },
      { key: 'area', header: 'Area', primary: true },
      { key: 'notes', header: 'Notes' },
    ],
  },
];

const sectionHasData = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return false;

  return rows.some((row) =>
    Object.entries(row || {}).some(([key, value]) => key !== '_id' && value !== '' && value != null)
  );
};

const formatDateLabel = (report) => {
  const firstDate = new Date(report.date).toLocaleDateString('en-GB');

  if (Number(report.shift) !== 3) return firstDate;

  const secondDate = report.secondDate
    ? new Date(report.secondDate).toLocaleDateString('en-GB')
    : new Date(new Date(report.date).setDate(new Date(report.date).getDate() + 1)).toLocaleDateString('en-GB');

  return `${firstDate} – ${secondDate}`;
};

const Detail = ({ label, value }) => (
  <div>
    <dt className="text-xs uppercase tracking-wide text-muted-fg">{label}</dt>
    <dd className="mt-0.5 text-sm text-foreground">{value || '—'}</dd>
  </div>
);

const ViewShiftReport = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, role } = useContext(AuthContext);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const response = await api.get(`/shift-report/shift-reports/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setReport(response.data);
      } catch (fetchError) {
        setError('Failed to fetch report. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [id, token]);

  const visibleSections = useMemo(() => {
    if (!report) return [];
    return sectionConfig.filter((section) => sectionHasData(report[section.key]));
  }, [report]);

  const hasLegacyPdfFiles = useMemo(
    () => Array.isArray(report?.pdfFile) && report.pdfFile.length > 0,
    [report]
  );

  const handleUpdate = () => navigate('/add-shift-report', { state: { report } });

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/shift-report/shift-reports/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      navigate('/viewReports');
    } catch (deleteError) {
      setError('Failed to delete report. Please try again later.');
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <PageLoader label="Loading report…" />
      </AppShell>
    );
  }

  if (!report) {
    return (
      <AppShell>
        <Alert
          variant="error"
          title="Report not found"
          action={
            <Button variant="secondary" size="sm" onClick={() => navigate('/viewReports')}>
              Back to reports
            </Button>
          }
        >
          {error || 'This report may have been deleted.'}
        </Alert>
      </AppShell>
    );
  }

  return (
    <>
      <PageHeader
        title={`Shift ${report.shift} report`}
        subtitle={formatDateLabel(report)}
        backTo="/viewReports"
        actions={
          <>
            <Button
              variant="ghost"
              icon={PictureAsPdfOutlined}
              onClick={() => exportToPDF(report)}
            >
              PDF
            </Button>
            <Button
              variant="ghost"
              icon={TableChartOutlined}
              onClick={() => exportStyledExcel(report)}
            >
              Excel
            </Button>
            {role === 'admin' && (
              <Button variant="danger-soft" icon={DeleteOutline} onClick={() => setConfirmDelete(true)}>
                Delete
              </Button>
            )}
            <Button icon={EditOutlined} onClick={handleUpdate}>
              Update
            </Button>
          </>
        }
      />

      <AppShell>
        {error && (
          <Alert variant="error" className="mb-5">
            {error}
          </Alert>
        )}

        <section className="mb-6 rounded-lg border border-border bg-surface p-4 shadow-sm sm:p-5">
          <dl className="grid gap-4 sm:grid-cols-3">
            <Detail label="RGA in charge" value={report.inCharge} />
            <Detail label="Date" value={formatDateLabel(report)} />
            <Detail label="Shift time" value={report.shiftTime} />
          </dl>
          {report.scrArea && (
            <div className="mt-4 border-t border-border pt-3">
              <Badge variant="neutral" size="sm">{report.scrArea}</Badge>
            </div>
          )}
        </section>

        {visibleSections.length === 0 ? (
          <Alert variant="warning" title="Legacy report">
            This report predates structured section data.
            {hasLegacyPdfFiles ? ' Uploaded PDF files are still available in backend storage.' : ''}
          </Alert>
        ) : (
          <div className="space-y-8">
            {visibleSections.map((section) => (
              <section key={section.key}>
                <div className="mb-3 flex items-center gap-2">
                  <h2 className="text-sm font-semibold text-foreground">{section.title}</h2>
                  <Badge variant="neutral" size="sm">
                    {(report[section.key] || []).length}
                  </Badge>
                </div>
                <DataTable
                  columns={section.columns}
                  rows={report[section.key] || []}
                  caption={section.title}
                  emptyTitle="No entries"
                />
              </section>
            ))}
          </div>
        )}
      </AppShell>

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        loading={deleting}
        title="Delete shift report"
        message="This permanently removes the report and all of its sections. This cannot be undone."
        confirmLabel="Delete"
      />
    </>
  );
};

export default ViewShiftReport;
